import { normaliseError } from './error-normaliser';
import { HANDLED, patchNetwork } from './network-patcher';
import { getCodeFrame } from './snippet-resolver';
import type {
	MiniSentryEvent,
	RuntimeErrorEvent,
	NetworkErrorEvent,
	SentryStackFrame,
} from '../types';

let installed = false;

export interface MiniSentryOptions {
	parentOrigin?: string;           // ← optional
	snippetContext?: number;         // ± lines, default 5
	rateLimit?: { max: number; per: number }; // events / ms
	beforeSend?(evt: MiniSentryEvent): MiniSentryEvent | null;
}

export function initMiniSentry(opts: MiniSentryOptions = {}) {
	if (installed || typeof window === 'undefined') return;
	installed = true;

	const ORIGIN = opts.parentOrigin ?? '*';
	const CTX = opts.snippetContext ?? 5;
	const RATE = opts.rateLimit ?? { max: 10, per: 10_000 };

	/* ––– rate-limit window –––––––––––––––––––––––––––––––––––––––––– */
	let count = 0;
	setInterval(() => (count = 0), RATE.per);

	function dispatch(evt: MiniSentryEvent) {
		if (count >= RATE.max) return;
		count++;
		const finalEvt = opts.beforeSend ? opts.beforeSend(evt) : evt;
		if (finalEvt) window.parent?.postMessage(finalEvt, ORIGIN);
	}

	/* ––– runtime error handler –––––––––––––––––––––––––––––––––––––– */
	async function handleRuntimeError(err: unknown) {
		if (err && (err as any)[HANDLED]) return;
		const base = normaliseError(err);
		const candidate: SentryStackFrame | undefined = base.frames.find(
			(f) => f.file && f.lineNumber != null && f.column != null,
		);
		let snippet = null;
		if (candidate) {
			try {
				snippet = await getCodeFrame(candidate, CTX);
			} catch {
				/* ignore */
			}
		}

		const evt: RuntimeErrorEvent = {
			type: 'runtime-error',
			buildId: (globalThis as any).__NEXT_DATA__?.buildId ?? 'dev',
			ts: Date.now(),
			...base,
			snippet,
		};
		dispatch(evt);
	}

	/* ––– wire global listeners –––––––––––––––––––––––––––––––––––––– */
	window.addEventListener('error', (e) => handleRuntimeError(e.error || e));
	window.addEventListener('unhandledrejection', (e) => {
		if (e.reason && (e.reason as any)[HANDLED]) return;  // ← ignore duplicates
		handleRuntimeError(e.reason);
	});

	/* ––– patch fetch / XHR –––––––––––––––––––––––––––––––––––––––––– */
	patchNetwork((netEvt) => dispatch(netEvt));
}
